'use client'

import { useEffect, useRef, useTransition } from 'react'
import { FormFooterContext } from './FormFooterContext'
import { FormFooterCancel } from './FormFooterCancel'
import { FormFooterSubmit } from './FormFooterSubmit'
import { UseFormTrigger } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

export interface FormFooterProps {
  formTrigger?: UseFormTrigger<any>
  className?: string
  children: React.ReactNode
}

const FormFooterComponent = ({
  formTrigger,
  className,
  children,
}: FormFooterProps) => {
  let [isPending, startTransition] = useTransition()
  const footerRef = useRef<HTMLDivElement>(null)

  // The bar is `fixed`, so it takes up no space in the flow and covers the tail
  // of whatever page it sits on. Reserve its height at the end of the document
  // instead of making every caller hand-roll a matching `pb-*`. Measured rather
  // than hardcoded because the buttons wrap on narrow viewports.
  useEffect(() => {
    const footer = footerRef.current
    if (!footer) return

    const reserveSpace = () => {
      document.body.style.paddingBottom = `${footer.offsetHeight}px`
    }
    reserveSpace()

    const observer = new ResizeObserver(reserveSpace)
    observer.observe(footer)

    return () => {
      observer.disconnect()
      document.body.style.paddingBottom = ''
    }
  }, [])

  return (
    <FormFooterContext.Provider
      value={{
        formTrigger,
        isPending,
        startTransition,
      }}
    >
      <div
        ref={footerRef}
        className={twMerge(
          'fixed bottom-0 inset-x-0 flex bg-white px-8 py-4 border-t border-gray-200 md:left-72 z-50',
          className
        )}
      >
        {children}
      </div>
    </FormFooterContext.Provider>
  )
}

export default Object.assign(FormFooterComponent, {
  Cancel: FormFooterCancel,
  Submit: FormFooterSubmit,
})
